/* eslint-disable no-console */
import { useEffect, useState } from "react";
import {
  convertGoogleDriveFileStructureToUppyFile,
  convertGoogleDriverFolderFileToGoogleDriveFile,
} from "./google-types";
import { toast } from "react-toastify";
import { removeDuplicatesFromArray } from "./utils";
import React from "react";
import { load } from "webfontloader";
import { create } from "lodash";

const GooglePicker = ({
  onSelectFiles,
  setGoogleAccessTokenInContext,
  googleAccessTokenFromContext,
  singleFile = false,
}) => {
  const clientId = `${process.env["REACT_APP_GOOGLE_CLIENT_ID"]}`;
  const devKey = `${process.env["REACT_APP_GOOGLE_DEV_KEY"]}`;

  const [pickerInited, setPickerInited] = useState(false);
  const [gisInited, setGisInited] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const scopes = ["https://www.googleapis.com/auth/drive.file"];


  const loadGoogleIdentityServices = () => {
    // console.log("Loading Google Identity Services...");
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = resolve;
      script.onerror = (err) => {
        console.log(err);
        reject();
      };
      document.head.appendChild(script);
    });
  };

  const loadGoogleApis = () => {
    // console.log("Loading Google APIs...");
    return new Promise((resolve, reject) => {
      const pickerScript = document.createElement("script");
      pickerScript.src = "https://apis.google.com/js/api.js";
      pickerScript.async = true;
      pickerScript.onload = () => {
        window.gapi.load("picker", () => {
          setPickerInited(true);
          // console.log("Google Picker Loaded!");
          resolve();
        });
      };
      pickerScript.onerror = (err) => {
        console.log(err);
        reject();
      };
      document.head.appendChild(pickerScript);
    });
  };

  const loadAllApis = async () => {
    // console.log("Loading All APIs...");
    await loadGoogleIdentityServices();
    await loadGoogleApis();

    const theTokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: scopes.join(" "),
      callback: (response) => {
        // console.log("Token response:", response);
        setAccessToken(response.access_token);
      },
    });
  
    setTokenClient(theTokenClient);
    setGisInited(true);
  };

  useEffect(() => {
    // NOTE: While scripts are messy, they are more reliable than packages out there due to how poorly documented this whole thing is
    loadAllApis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (gisInited && pickerInited) {
      createPicker();
    }
  }, [gisInited, pickerInited]);
  
  useEffect(() => {
    if (tokenClient && accessToken && pickerInited) {
      showPicker();
    }
  }, [tokenClient,accessToken, pickerInited]);

  // const validateToken = async (accessToken) => {
  //   try {
  //     if (!tokenClient) {
  //       return;
  //     }
  //     const response = await fetch(
  //       `https://www.googleapis.com/oauth2/v3/tokeninfo?access_token=${accessToken}`
  //     );
  //     const data = await response.json();

  //     if (data.error_description || data.error) {
  //       tokenClient.callback = async (response) => {
  //         if (response.error !== undefined) {
  //           throw response;
  //         }
  //         if (setGoogleAccessTokenInContext) {
  //           setGoogleAccessTokenInContext(response.access_token);
  //         }
  //         showPicker(response.access_token);
  //       };
  //       tokenClient.requestAccessToken({ prompt: "consent" });

  //       return false;
  //     }
  //     return true;
  //   } catch (error) {
  //     console.error("Failed to validate token:", error);
  //     return false;
  //   }
  // };

  const showPicker = async () => {
    // console.log({ accessToken, info: "showPicker" });
    try {
      const docsView = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setSelectFolderEnabled(true);
  
      const picker = new window.google.picker.PickerBuilder()
        .addView(docsView)
        .enableFeature(
          singleFile === false
            ? window.google.picker.Feature.MULTISELECT_ENABLED
            : null
        )
        .setOrigin(window.location.protocol + "//" + window.location.host)
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
        .setOAuthToken(accessToken)
        .setDeveloperKey(devKey)
        .setCallback(async (e) => pickerCallback(e, accessToken))
        .setAppId(process.env.REACT_APP_GOOGLE_PICKER_PROJECT_ID)
        .build();
  
      picker.setVisible(true);
    } catch (error) {
      console.log(error);
    }
  };

  const createPicker = async () => {
    // console.log("Creating Picker...");
    if (!pickerInited || !gisInited || !tokenClient) return;

    // Sign in
    if (!accessToken) {
      // console.log({ tokenClient, info: "createPicker" });
      tokenClient.requestAccessToken({ prompt: "consent" });
      return;
    }
    // If accessToken is available, show the picker
    showPicker();
  };

  const listFilesInFolder = async (folderId, visitedFolderIds = [folderId]) => {
    const filesToPush = [];
    const folderFilesResponse = await window.gapi.client.drive.files.list({
      q: `'${folderId}' in parents and trashed = false`,
      fields: "files(*)",
    });

    const files = folderFilesResponse.result.files;
    if (files && files.length > 0) {
      await Promise.all(
        files.map(async (file) => {
          if (
            file.mimeType.includes("folder") ||
            (file.shortcutDetails && file.shortcutDetails.targetMimeType && file.shortcutDetails.targetMimeType.includes("folder"))
          ) {
            const nestedFolderId = 
              (file.shortcutDetails && file.shortcutDetails.targetMimeType && file.shortcutDetails.targetMimeType.includes("folder"))
                  ? file.shortcutDetails.targetId
                  : file.id;
            if (!visitedFolderIds.includes(nestedFolderId)) {
              const nestedFolderFiles = await listFilesInFolder(
                nestedFolderId,
                [...visitedFolderIds, nestedFolderId]
              );
              filesToPush.push(...nestedFolderFiles);
            }
          } else {
            filesToPush.push(file);
          }
        })
      );
    } else {
      toast.error("No Files Found!");
    }

    return filesToPush;
  };

  const pickerCallback = async (data) => {
    // console.log({ data, info: "pickerCallback" });
    if (
      data[window.google.picker.Response.ACTION] ===
      window.google.picker.Action.PICKED
    ) {
      const pickedDocs = data[window.google.picker.Response.DOCUMENTS];
      const failedPicks = pickedDocs.filter(doc => doc.driveSuccess === false); 
      const successPicks = pickedDocs.filter(doc => doc.driveSuccess === true); 
      // console.log( { failedPicks, successPicks });
      const url = pickedDocs[window.google.picker.Document.URL];

      const docs = await Promise.all(
        pickedDocs.map(async (doc) => {
          if (doc.type === "folder") {
            const folderId = doc.id;
            const files = await listFilesInFolder(folderId);
            const convertedFolderFiles = files.map((f) => convertGoogleDriverFolderFileToGoogleDriveFile(f));
            return convertedFolderFiles;
          } else {
            return doc;
          }
        })
      );

      const flatDocs = docs.flat(1);

      const convertedDocuments = flatDocs.map((doc) => {
        return convertGoogleDriveFileStructureToUppyFile(doc);
      });
      const convertedUniqueDocuments = removeDuplicatesFromArray(convertedDocuments);
      onSelectFiles(convertedUniqueDocuments, accessToken);
      // console.log("Picked file URL:", url, "DOC: ", flatDocs);
    }
  };

  return (
    <button
      onClick={() => showPicker()}
      className="flex-1 h-full w-full flex items-center justify-center"
    >
      Upload From Google Drive
    </button>
  );
};

export default GooglePicker;
